/**
 * How an account signs in, as the single value every user-facing surface shows:
 * the Users grid, the user-edit page, and the "Run as user" picker.
 *
 * Mirrors the server's `UserAccountType` (Nexo.Domain.Shared/Sso). Two paths
 * produce it and they must agree. The user-edit page derives it here from the
 * raw markers ABP's stock /api/identity/users carries on `extraProperties`; the
 * Users grid and the run-as picker receive it already derived, because their
 * endpoints project a DTO rather than the user entity. Same four values, same
 * precedence — support ahead of the authentication class — so a user cannot
 * read one way on the edit page and another in the grid or the picker.
 */
export type UserAccountType = 'Local' | 'Entra' | 'BreakGlass' | 'Support';

/** Extra-property keys on IdentityUserDto (Nexo.Domain.Shared/Sso/NexoIdentityMarkerNames). */
export const AUTHENTICATION_CLASS_PROPERTY = 'Nexo.AllowedAuthenticationClass';
export const SUPPORT_IDENTITY_PROPERTY = 'Nexo.IsSupportIdentity';

/**
 * Read the account type off an IdentityUserDto's extra properties.
 *
 * Absence means Local, exactly as the server reads it: every account created
 * before the SSO feature carries no marker at all, and "unmarked" is a known
 * state rather than an unknown one.
 */
export function resolveUserAccountType(
  extraProperties: Record<string, unknown> | undefined,
): UserAccountType {
  // Support first: every support identity is Entra-class underneath, so reading
  // the class alone would show the vendor's support accounts as ordinary
  // customer employees — the one distinction the column exists for.
  if (extraProperties?.[SUPPORT_IDENTITY_PROPERTY] === true) return 'Support';

  const authenticationClass = extraProperties?.[AUTHENTICATION_CLASS_PROPERTY];
  if (authenticationClass === 'Entra') return 'Entra';
  if (authenticationClass === 'BreakGlass') return 'BreakGlass';
  return 'Local';
}

/** Narrow a server-provided string (the Users grid's and run-as picker's `accountType`) to a known type. */
export function parseUserAccountType(value: string | undefined | null): UserAccountType | null {
  return value === 'Local' || value === 'Entra' || value === 'BreakGlass' || value === 'Support'
    ? value
    : null;
}
